import { useCallback, useEffect, useRef, useState, forwardRef } from "react";
import type {
  MethodsOfUseForm,
  OptionType,
  WithPagination,
} from "../../types/types";
import { CreatableSelectBox } from "../CreateableSelectBox/CreatableSelectBox";
import type { SelectBoxProps } from "../SelectBoxV2/SelectBoxV2";
import { useDebounce } from "../../util/hooks";
import { useSWRInfinite } from "swr";
import type { InputActionMeta } from "react-select";
import type { AxiosError } from "axios";
import { useNotifyErrorOnce } from "../Notifications/useNotifyErrorOnce";
import { useTranslation } from "react-i18next";
import type { AttributeSchema } from "../../types/types.PIM";
import { hasValidValue } from "../SelectBoxV2/SelectBoxV2.utils";
import { constructInfiniteScrollQuery } from "../../util/paginatedInfiniteScrollQuery";
import { fetcher } from "../../util/util";
import { v4 as uuidv4 } from "uuid";

export const SearchSelectInfiniteScroll = forwardRef(
  <ResponseType extends {}, OptionValue extends string | {}>(
    props: {
      baseUrl: string | null;
      getOptions: (response: ResponseType[]) => OptionType<OptionValue>[];
      params?: URLSearchParams;
      normalPlaceholder?: boolean;
      options?: OptionType<OptionValue>[];
      showValue?: boolean;
      cacheScope?: string;
    } & Omit<SelectBoxProps, "options">,
    ref: React.Ref<any>
  ) => {
    const {
      baseUrl,
      getOptions,
      params: existingParams,
      options: selectBoxDefaultOptions = [],
      showValue = true,
      cacheScope: cacheScopeProp,
      ...selectBoxProps
    } = props;
    const instanceIdRef = useRef(uuidv4());
    const cacheScopeValue =
      cacheScopeProp ??
      (selectBoxProps.name
        ? `${String(selectBoxProps.name)}:${instanceIdRef.current}`
        : undefined);
    const cacheScope = cacheScopeValue
      ? encodeURIComponent(String(cacheScopeValue))
      : undefined;
    const { t } = useTranslation();
    const [searchQuery, setSearchQuery] = useState("");
    const [options, setOptions] = useState<OptionType<OptionValue>[]>(
      selectBoxDefaultOptions
    );
    const [internalValue, setInternalValue] = useState(
      selectBoxProps.defaultValue ?? null
    );
    const [debouncedSearchQuery] = useDebounce(searchQuery, 1000);
    const isControlled = selectBoxProps.value !== undefined;
    const normalizedValue = hasValidValue(
      isControlled ? selectBoxProps.value : internalValue
    );

    const handleChange = (val: any, action: any) => {
      const normalized = selectBoxProps.isMulti ? val ?? [] : val ?? null;
      if (!isControlled) {
        setInternalValue(normalized);
      }
      selectBoxProps.onChange?.(normalized, action);
    };

    const {
      data,
      error: dataError,
      setSize,
    } = useSWRInfinite<WithPagination<{ data: ResponseType[] }>>(
      (index, previousPageData) => {
        if (!baseUrl) {
          return null;
        }

        const url = constructInfiniteScrollQuery({
          baseUrl,
          existingParams,
          query: debouncedSearchQuery,
          previousPageData,
          index,
        });

        if (!url) {
          return null;
        }

        return [url, cacheScope ?? ""];
      },
      (url: string) => fetcher<WithPagination<{ data: ResponseType[] }>>(url)
    );

    /**
     * swr@0.3 serializes a null key to the shared `""` cache slot, so calling
     * `setSize` while the key function returns null (i.e. no `baseUrl`) writes
     * bookkeeping into that slot — which every other idle `useSWRInfinite` in
     * the app then reads back as its own `data`/`error`. Paging is meaningless
     * without a `baseUrl` anyway, so skip it.
     */
    const setPageSize = useCallback(
      (size: number | ((size: number) => number)) => {
        if (!baseUrl) {
          return;
        }
        void setSize(size);
      },
      [baseUrl, setSize]
    );

    const handleInputChange = (query: string, actionMeta: InputActionMeta) => {
      switch (actionMeta.action) {
        case "menu-close":
          setSearchQuery("");
          return;
        case "input-change":
          setSearchQuery(query);
          setPageSize(1);
          return;
      }
      setPageSize(1);
    };

    // swr@0.3's setSize always revalidates, so only reset paging when the
    // query is cleared — not on mount, where it would refetch page 1 twice.
    const previousSearchQuery = useRef(searchQuery);
    useEffect(() => {
      const wasCleared =
        searchQuery === "" && previousSearchQuery.current !== "";
      previousSearchQuery.current = searchQuery;
      if (wasCleared) {
        setPageSize(1);
      }
    }, [searchQuery, setPageSize]);

    useNotifyErrorOnce(
      baseUrl
        ? (dataError as AxiosError)?.response?.data?.message ??
            t("Could not load search options. Please try again later.")
        : undefined,
      baseUrl ? dataError : undefined
    );

    const isLoading = baseUrl ? !data && !dataError : false;

    useEffect(() => {
      if (data && data.length) {
        setOptions(data.flatMap(({ data }) => getOptions(data)));
      }
    }, [data, getOptions]);

    return (
      <CreatableSelectBox
        {...(selectBoxProps as SelectBoxProps)}
        menuPlacement={selectBoxProps.menuPlacement ?? "auto"}
        key={props.key}
        name={selectBoxProps.name}
        inputValue={searchQuery}
        value={showValue ? normalizedValue : null}
        onChange={handleChange}
        isSearchBar
        controlShouldRenderValue={selectBoxProps.isMulti ? false : true}
        normalPlaceholder={props.normalPlaceholder}
        isClearable={(selectBoxProps.isClearable ?? false) && !!normalizedValue}
        options={
          selectBoxDefaultOptions && !baseUrl
            ? selectBoxDefaultOptions.filter(
                (obj, index, self) =>
                  index ===
                  self.findIndex((option) => option.value === obj.value)
              )
            : options?.filter(
                (obj, index, self) =>
                  index ===
                  self.findIndex((option) => option.value === obj.value)
              )
        }
        onMenuScrollToBottom={() => setPageSize((size) => size + 1)}
        onInputChange={handleInputChange}
        isLoading={isLoading}
        filterOption={null}
        loadingMessage={() => t("Please wait...")}
        ref={ref}
      />
    );
  }
);

export function SearchMultiSelectCell({
  baseUrl,
  params,
  attribute,
  methodsOfUseForm,
  value,
  isDisabled,
  ...rest
}: {
  baseUrl: string | null;
  params?: URLSearchParams;
  attribute: AttributeSchema;
  methodsOfUseForm: MethodsOfUseForm;
  value:
    | string
    | null
    | boolean
    | number
    | { name: string }[]
    | { display_text: string; url: string };
  isDisabled?: boolean;
}) {
  return <div></div>;
}
